import { useEffect, useRef, useState } from 'react';
import { BookOpen, ChevronDown, LogOut, User as UserIcon, Settings } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { logout, logoutEverywhere } from '@/lib/api';
import { Modal } from '../Modal';
import formStyles from '../Form.module.css';
import styles from './UserChipMenu.module.css';

interface UserChipMenuProps {
  onNavigate: (path: string) => void;
}

/**
 * User chip — avatar + name + chevron, wired to a dropdown with
 * profile / settings / logout. Replaces the non-interactive
 * <div className="userChip"> on the old AppShell — same visual
 * shape, but the whole chip is now an actual button.
 */
/**
 * Where this persona's Settings lives, or null when they have none.
 *
 * '/settings' is the OPERATOR's tenant settings, gated by
 * requireTenantScope — so it 403s for every templated persona. A menu
 * item that always 403s is worse than no item.
 */
function settingsHrefFor(template: string | null | undefined, role: string | undefined): string | null {
  if (template === 'building_admin' || template === 'charger_owner' || template === 'property_manager') {
    return '/site-settings';
  }
  // fleet_admin and sales_agent have no Settings surface yet. They get
  // no item until they do — see the Fleet and Partner steps.
  if (template === 'sales_agent' || role === 'fleet_admin' || role === 'third_party_admin') return null;
  return '/settings';
}

export function UserChipMenu({ onNavigate }: UserChipMenuProps) {
  const user = useAuthStore((s) => s.user);
  const settingsHref = user?.facilities_only ? null : settingsHrefFor(user?.template, user?.role);
  const [open, setOpen] = useState(false);
  // In-app logout confirm — admin previously logged out on the first
  // click with no confirmation at all; this adds the same styled
  // "Log out?" step the PWA gets, via the existing Modal primitive.
  const [confirmLogout, setConfirmLogout] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // ESC + click-outside close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (wrapRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  // Log out ends this tab's session only; this ends every session the
  // account holds (both apps, every device), for a lost or shared device.
  const [everywhereState, setEverywhereState] = useState<'idle' | 'working' | 'failed'>('idle');
  const handleLogoutEverywhere = async () => {
    setEverywhereState('working');
    if (await logoutEverywhere()) { window.location.href = '/login'; return; }
    setEverywhereState('failed');
  };

  const handleNavigate = (path: string) => {
    onNavigate(path);
    setOpen(false);
  };

  const initial = user?.name?.[0]?.toUpperCase() ?? 'U';
  const name = user?.name ?? 'Admin';

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <button
        type="button"
        className={`${styles.chip} ${open ? styles.chipOpen : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Account menu for ${name}`}
        title={name}
      >
        <span className={styles.avatar}>{initial}</span>
        <span className={styles.name}>{name}</span>
        <ChevronDown size={12} className={`${styles.chev} ${open ? styles.chevOpen : ''}`} />
      </button>

      {open && (
        <div className={styles.panel} role="menu">
          <div className={styles.identity}>
            <span className={styles.identityName}>{name}</span>
            {user?.email && <span className={styles.identityEmail}>{user.email}</span>}
          </div>
          <a className={styles.item} href="/guides/evi-v3-onboarding.html" target="_blank" rel="noreferrer" role="menuitem" onClick={() => setOpen(false)}>
            <BookOpen size={14} />
            <span>Onboarding guides</span>
          </a>
          {(user?.is_sales_agent || user?.template === 'sales_agent') && <a className={styles.item} href="mailto:partners@evinitiative.com" role="menuitem" onClick={() => setOpen(false)}>Contact support</a>}
          <div className={styles.divider} />
          {!user?.facilities_only && <button
            type="button"
            className={styles.item}
            onClick={() => handleNavigate('/profile')}
            role="menuitem"
          >
            <UserIcon size={14} />
            <span>Profile</span>
          </button>}
          {/* Settings is PERSONA-ROUTED, and hidden where there is
              nothing to route to.

              This item used to be ungated and always pointed at
              '/settings' — the OPERATOR tenant-settings page, whose
              first queries go through requireTenantScope. So every
              Site, Fleet and Sales Partner user carried a Settings
              item in their chrome that 403s, outside the sidebar where
              no visibility filter could see it. The nav audit found it
              only by reading the component.

              The Site persona now has its own Settings page and goes
              there. Fleet and SP have none yet, so they get no item
              rather than a broken one; when theirs exist, they become
              rows here. */}
          {settingsHref && (
            <button
              type="button"
              className={styles.item}
              onClick={() => handleNavigate(settingsHref)}
              role="menuitem"
            >
              <Settings size={14} />
              <span>Settings</span>
            </button>
          )}
          <div className={styles.divider} />
          <button
            type="button"
            className={`${styles.item} ${styles.itemDanger}`}
            onClick={() => { setOpen(false); setConfirmLogout(true); }}
            role="menuitem"
          >
            <LogOut size={14} />
            <span>Log out</span>
          </button>
        </div>
      )}

      <Modal
        isOpen={confirmLogout}
        onClose={() => { setConfirmLogout(false); setEverywhereState('idle'); }}
        title="Log out?"
        size="sm"
        footer={
          <>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={() => { setConfirmLogout(false); setEverywhereState('idle'); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={formStyles.btnSecondary}
              disabled={everywhereState === 'working'}
              onClick={() => { void handleLogoutEverywhere(); }}
            >
              Log out of all devices
            </button>
            <button
              type="button"
              className={formStyles.btnPrimary}
              onClick={() => { void handleLogout(); }}
            >
              Log out
            </button>
          </>
        }
      >
        {/* Explicit body-text token: the modal mounts inside the nav's
            dark chrome (hardcoded #f4f4f5 text), so without this the
            paragraph inherits near-white and vanishes on the
            light-mode modal surface. var(--color-tx) flips per theme. */}
        <p style={{ margin: 0, color: 'var(--color-tx)' }}>
          You&apos;ll be signed out of the EVI admin console. Log out of all devices also ends your sessions in the
          driver app and on every other device.
        </p>
        {everywhereState === 'failed' && (
          <p role="alert" style={{ margin: '8px 0 0', color: 'var(--color-error)' }}>
            Your other devices could not be logged out. Please try again.
          </p>
        )}
      </Modal>
    </div>
  );
}
